/* eslint react/no-multi-comp: 0, react/prop-types: 0, react/no-unused-prop-types: 0 */
import React from 'react';
import { Form, type FormItemProps } from 'antd';

import { formGroupTokens } from '../theme/tokens';

// MVC-native form field group. It WRAPS AntD `Form.Item`: the label, required
// marker, help/hint text, validation status, and field layout all come from
// `Form.Item`. We only layer owned styling and a slot-based API on top.
//
// `FormGroupLabel` and `FormGroupHint` are thin slots — they do not render
// their own field DOM. `FormGroup` reads them out of its children and feeds
// their content into `Form.Item`'s `label` and `help` props, so the rendered
// field is a real `Form.Item` and the control children pass straight through.
//
// This primitive owns field presentation only — not Form data binding,
// validation rules, product workflow, or domain logic.

export type FormGroupContext = 'primary' | 'secondary' | 'danger';

// Owned semantic `context` maps to `Form.Item`'s `validateStatus`. Only the
// danger context surfaces an error status; primary/secondary are resting
// display states with no validation styling.
const validateStatusFor = (
    context: FormGroupContext,
): FormItemProps['validateStatus'] =>
    context === 'danger' ? 'error' : undefined;

export type FormGroupLabelProps = {
    /** Label text content. */
    text?: React.ReactNode;
    /** Marks the field required (renders `Form.Item`'s required marker). */
    required?: boolean;
    children?: React.ReactNode;
};

// Label slot. Rendered as a styled span that becomes `Form.Item`'s `label`,
// styled to read like the legacy gds form-group label it replaces — small,
// uppercase, secondary-toned, bold. `Form.Item` owns label placement and the
// required marker; this only styles the label text.
const FormGroupLabel: React.FC<FormGroupLabelProps> = ({ text, children }) => (
    <span
        style={{
            textTransform: formGroupTokens.labelTextTransform,
            fontWeight: formGroupTokens.labelFontWeight,
            letterSpacing: formGroupTokens.labelLetterSpacing,
        }}
    >
        {text ?? children}
    </span>
);

FormGroupLabel.displayName = 'FormGroupLabel';

export type FormGroupHintProps = {
    children?: React.ReactNode;
};

// Hint slot. Its content becomes `Form.Item`'s `help`, so `Form.Item` owns
// hint placement and the error coloring that follows `validateStatus`.
const FormGroupHint: React.FC<FormGroupHintProps> = ({ children }) => (
    <>{children}</>
);

FormGroupHint.displayName = 'FormGroupHint';

const isSlot = (
    node: React.ReactNode,
    slot: React.FC<any>,
): node is React.ReactElement =>
    React.isValidElement(node) && node.type === slot;

export type FormGroupProps = Omit<
    FormItemProps,
    'label' | 'help' | 'required' | 'validateStatus'
> & {
    /** Field state. `danger` maps to `Form.Item` error status. Default `primary`. */
    context?: FormGroupContext;
    /** Disables the field group (propagated to the field via `Form.Item`). */
    disabled?: boolean;
    /** Convenience label slot; prefer a `<FormGroupLabel>` child. */
    label?: React.ReactNode;
    /** Convenience required flag; a `<FormGroupLabel required>` child also sets it. */
    required?: boolean;
    /** Convenience hint slot; prefer a `<FormGroupHint>` child. */
    hint?: React.ReactNode;
    children?: React.ReactNode;
};

type FormGroupComponent = React.ForwardRefExoticComponent<
    FormGroupProps & React.RefAttributes<HTMLDivElement>
>;

// Reads `FormGroupLabel` / `FormGroupHint` slots out of children and routes
// their content into `Form.Item`'s `label` / `help`, then renders the
// remaining children (the control) as `Form.Item`'s child. Anything passed via
// the `label` / `hint` / `required` props is used as a fallback.
const FormGroup = React.forwardRef<HTMLDivElement, FormGroupProps>(
    (
        {
            context = 'primary',
            disabled,
            label,
            hint,
            required,
            children,
            ...props
        },
        _ref,
    ) => {
        let slotLabel: React.ReactNode = label;
        let slotHint: React.ReactNode = hint;
        let slotRequired = required ?? false;

        const controlChildren = React.Children.toArray(children).filter(
            child => {
                if (isSlot(child, FormGroupLabel)) {
                    const labelProps = child.props as FormGroupLabelProps;
                    slotLabel = child;
                    if (labelProps.required) slotRequired = true;
                    return false;
                }
                if (isSlot(child, FormGroupHint)) {
                    slotHint = child;
                    return false;
                }
                return true;
            },
        );

        const item = (
            <Form.Item
                label={slotLabel}
                help={slotHint}
                required={slotRequired}
                validateStatus={validateStatusFor(context)}
                {...props}
            >
                {controlChildren}
            </Form.Item>
        );

        // `disabled` is a presentation state layered on top of `Form.Item`
        // (matching the legacy dimmed field group); the control still owns its
        // own interactive disabled prop.
        return disabled ? (
            <div
                ref={_ref}
                style={{ opacity: formGroupTokens.disabledOpacity }}
                aria-disabled
            >
                {item}
            </div>
        ) : (
            <div ref={_ref}>{item}</div>
        );
    },
) as FormGroupComponent;

FormGroup.displayName = 'FormGroup';

export { Form as AntdForm, FormGroupLabel, FormGroupHint };
export default FormGroup;
